import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  triggerExternalAnalysis,
  retryExternalAnalysis,
  ExternalAnalysisError,
} from '../api/externalAnalysis';

interface ExternalAnalysisPanelProps {
  projectId: string;
  scriptReady: boolean;
  pronounsResolved: boolean;
  activeTaskId: string | null;
  lastFailureReason?: string | null;
  onTriggered: (taskId: string) => void;
}

const ERROR_CODE_KEYS: Record<string, string> = {
  pronoun_resolve_required: 'externalAnalysis.errorPronounResolveRequired',
  analysis_in_progress: 'externalAnalysis.errorAnalysisInProgress',
  script_required: 'externalAnalysis.errorScriptRequired',
  insufficient_credit: 'externalAnalysis.errorInsufficientCredit',
  internal_error: 'externalAnalysis.errorInternalError',
};

const ExternalAnalysisPanel: React.FC<ExternalAnalysisPanelProps> = ({
  projectId,
  scriptReady,
  pronounsResolved,
  activeTaskId,
  lastFailureReason,
  onTriggered,
}) => {
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isRetry = !!activeTaskId;
  const guardsOk = scriptReady && pronounsResolved;
  const disabled = loading || !guardsOk;

  const guardHint = (() => {
    if (!scriptReady) return t('externalAnalysis.guardScriptRequired');
    if (!pronounsResolved) return t('externalAnalysis.guardPronounRequired');
    return null;
  })();

  const handleClick = async () => {
    if (disabled) return;
    setLoading(true);
    setError(null);
    try {
      const result = isRetry
        ? await retryExternalAnalysis(projectId)
        : await triggerExternalAnalysis(projectId);
      onTriggered(result.taskId);
    } catch (e) {
      if (e instanceof ExternalAnalysisError) {
        const key = e.code && ERROR_CODE_KEYS[e.code];
        const friendly = key ? t(key) : (e.message || t('startFailed'));
        setError(friendly);
      } else if (e instanceof Error) {
        setError(e.message || t('startFailed'));
      } else {
        setError(t('startFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: '6px',
        padding: '0.75rem 1rem',
        marginTop: '0.75rem',
        background: 'var(--bg-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <h4 style={{ margin: 0, fontSize: '0.95rem' }}>{t('externalAnalysis.panelTitle')}</h4>
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          style={{
            background: 'var(--primary)',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 14px',
            fontSize: '0.85rem',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          {loading && <span className="loading-spinner" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />}
          {loading ? t('externalAnalysis.starting') : isRetry ? t('retry') : t('startAnalysis')}
        </button>
        {scriptReady && !pronounsResolved && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              color: 'var(--text-3)',
              fontSize: '0.85rem',
            }}
          >
            <span className="loading-spinner" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
            {t('externalAnalysis.waitPronounResolve')}
          </span>
        )}
      </div>
      {isRetry && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--text-3)' }}>
          {lastFailureReason
            ? t('externalAnalysis.lastFailedWithReason', { reason: lastFailureReason })
            : t('externalAnalysis.lastFailed')}
        </div>
      )}
      {guardHint && !(scriptReady && !pronounsResolved) && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--text-3)' }}>
          {guardHint}
        </div>
      )}
      {error && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--danger)' }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default ExternalAnalysisPanel;
